import React, { useCallback, useEffect, useMemo } from 'react';
import { Form, Upload as UploadAntd } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import { Popup, Upload, notification, NOTIFICATION_TYPES } from '_atoms';
import type { UploadFile } from 'antd/es/upload/interface';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
const MAX_FILES = 5;

interface AttachFormatDefinition {
  extensions: string[];
  mimes: string[];
}

/**
 * Registry of format ids → extensions + MIME types.
 * Use `allowedFormats` prop with these ids (e.g. ['pdf', 'jpg']).
 */
export const POPUP_ATTACH_FORMAT_DEFINITIONS: Record<string, AttachFormatDefinition> = {
  pdf: { extensions: ['.pdf'], mimes: ['application/pdf'] },
  pptx: {
    extensions: ['.pptx'],
    mimes: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
  },
  doc: { extensions: ['.doc'], mimes: ['application/msword'] },
  docx: {
    extensions: ['.docx'],
    mimes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  },
  jpg: { extensions: ['.jpg', '.jpeg'], mimes: ['image/jpeg'] },
  png: { extensions: ['.png'], mimes: ['image/png'] },
  ppt: { extensions: ['.ppt'], mimes: ['application/vnd.ms-powerpoint'] },
};

/** Default when `allowedFormats` is omitted (e.g. candidate profile attach — includes legacy .ppt). */
export const POPUP_ATTACH_DEFAULT_FORMATS = Object.keys(POPUP_ATTACH_FORMAT_DEFINITIONS);

/** Timeline comment attach: pdf, pptx, doc, docx, jpg, png */
export const POPUP_ATTACH_TIMELINE_FORMATS = ['pdf', 'pptx', 'doc', 'docx', 'jpg', 'png'];

const resolveAttachmentRule = (formatKeys?: string[]) => {
  const known = new Set(Object.keys(POPUP_ATTACH_FORMAT_DEFINITIONS));
  const keys = (formatKeys?.length ? formatKeys : POPUP_ATTACH_DEFAULT_FORMATS).filter(k => known.has(k));

  const safeKeys = keys.length ? keys : POPUP_ATTACH_DEFAULT_FORMATS;

  const extensions: string[] = [];
  const mimes: string[] = [];
  safeKeys.forEach(id => {
    const def = POPUP_ATTACH_FORMAT_DEFINITIONS[id];
    extensions.push(...def.extensions);
    mimes.push(...def.mimes);
  });

  const uniqueExt = Array.from(new Set(extensions));
  const uniqueMime = Array.from(new Set(mimes));
  const accept = [...uniqueExt, ...uniqueMime].join(',');
  const label = safeKeys.map(k => k.toUpperCase()).join(', ');

  return { extensions: uniqueExt, mimes: uniqueMime, accept, label };
};

interface PopupAttachFileProps {
  open: boolean;
  list: UploadFile[];
  onClose: () => void;
  onAttach: (values: { files: UploadFile[] }) => void;
  allowedFormats?: string[];
}

export const PopupAttachFile: React.FC<PopupAttachFileProps> = ({ open, list, onClose, onAttach, allowedFormats }) => {
  const { t } = useTranslation();

  const { extensions, mimes, accept, label } = useMemo(() => resolveAttachmentRule(allowedFormats), [allowedFormats]);

  const [form] = useForm();

  const hasNewFiles = !!(Form.useWatch('files', form) as UploadFile[] | undefined)?.filter(file => file.originFileObj)
    .length;

  const isFileFormatValid = useCallback(
    (file: File) => {
      const isValidMimeType = mimes.includes(file.type);
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      const isValidExtension = extensions.includes(fileExtension);
      return isValidMimeType || isValidExtension;
    },
    [extensions, mimes]
  );

  const validateFile = useCallback(
    (file: File) => {
      if (file.size > MAX_FILE_SIZE) {
        notification({
          type: NOTIFICATION_TYPES.ERROR,
          message: 'File size exceeds 50 MB limit.',
        });
        return false;
      }

      if (!isFileFormatValid(file)) {
        notification({
          type: NOTIFICATION_TYPES.ERROR,
          message: `Unsupported file format. Allowed: ${label}.`,
        });
        return false;
      }

      return true;
    },
    [isFileFormatValid, label]
  );

  useEffect(() => {
    form.setFieldValue('files', list);
  }, [open, list, form]);

  const handleBeforeUpload = useCallback(
    (file: File, batch: File[]) => {
      const isValid = validateFile(file);
      if (!isValid) return UploadAntd.LIST_IGNORE;

      const currentFiles = form.getFieldValue('files') || [];
      // Count files that precede the current one in this batch and are valid,
      // because antd calls beforeUpload synchronously for every file in a batch
      // with the same (stale) form value — we can't rely on form state alone.
      const validBeforeInBatch = batch
        .slice(0, batch.indexOf(file))
        .filter(f => f.size <= MAX_FILE_SIZE && isFileFormatValid(f)).length;

      if (currentFiles.length + validBeforeInBatch >= MAX_FILES) {
        if (currentFiles.length + validBeforeInBatch === MAX_FILES) {
          notification({
            type: NOTIFICATION_TYPES.ERROR,
            message: 'You can attach up to 5 files only.',
          });
        }
        return UploadAntd.LIST_IGNORE;
      }

      return false;
    },
    [form, validateFile, isFileFormatValid]
  );

  return (
    <Popup
      open={open}
      className="mobile-full-size"
      title={t('translation:popup_attach_file.title')}
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:popup_attach_file.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:popup_attach_file.button_confirm'),
        form: 'form-candidate-add-file',
        htmlType: 'submit',
        disabled: !hasNewFiles,
      }}
    >
      <Form form={form} onFinish={onAttach} name="form-candidate-add-file">
        <Upload
          itemProps={{ name: 'files' }}
          accept={accept}
          multiple
          beforeUpload={handleBeforeUpload}
          disableDelete={list.map(file => file.uid)}
        />
      </Form>
    </Popup>
  );
};
